import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthGate from "@/components/AuthGate";
import Header from "@/components/Header";
import CategoryTypeahead from "@/components/CategoryTypeahead";
import { toast, Toaster } from "sonner";
import { MapPin, ArrowLeft, MessageSquare, Pencil, XCircle, RotateCcw } from "lucide-react";

function CloseRequestModal({ requestId, responders, onClose, onDone }) {
  const [step, setStep] = useState("outcome");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const searchPros = async (term) => {
    setQ(term);
    if (!term.trim()) { setResults([]); return; }
    try { const r = await api.get("/wall/taggable-pros", { params: { q: term } }); setResults(r.data.pros || []); }
    catch { setResults([]); }
  };

  const submit = async (outcome, proUserId = null) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/requests/${requestId}/close`, { outcome, pro_user_id: proUserId });
      toast.success(r.data.status === "filled" ? "Request marked as filled" : "Request closed");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not close request"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="close-request-modal">
        {step === "outcome" ? (
          <>
            <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>How did this request end?</h3>
            <p className="text-sm text-[#222]/55 mb-4">You can always reopen it later.</p>
            <button onClick={() => setStep("who")} className="w-full text-left px-4 py-3 rounded-xl border border-black/10 hover:border-[#245544] font-semibold mb-2" data-testid="close-found-someone">I found someone</button>
            <button onClick={() => submit("no_longer_needed")} disabled={busy} className="w-full text-left px-4 py-3 rounded-xl border border-black/10 hover:border-[#245544] font-semibold disabled:opacity-50" data-testid="close-no-longer-needed">I no longer need this request</button>
          </>
        ) : (
          <>
            <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Who helped you?</h3>
            <p className="text-sm text-[#222]/55 mb-4">Choosing a LIMYA Pro lets you leave them a review.</p>
            {responders.length > 0 && (
              <>
                <p className="text-[11px] uppercase tracking-wider text-[#222]/45 mb-2">Responded to this request</p>
                <div className="space-y-1.5 mb-4">
                  {responders.map(p => (
                    <button key={p.user_id} onClick={() => submit("found_someone", p.user_id)} disabled={busy}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-black/10 hover:border-[#245544] text-left disabled:opacity-50"
                      data-testid={`close-pick-responder-${p.user_id}`}>
                      <span className="w-8 h-8 rounded-full bg-[#F2F4F2] grid place-items-center font-bold text-xs overflow-hidden shrink-0">
                        {p.avatar_url ? <img src={p.avatar_url} className="w-full h-full object-cover" alt=""/> : p.business_name?.[0]}
                      </span>
                      <span className="font-semibold text-sm truncate">{p.business_name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <p className="text-[11px] uppercase tracking-wider text-[#222]/45 mb-2">Find a Pro</p>
            <input value={q} onChange={e => searchPros(e.target.value)} placeholder="Search LIMYA Professionals…"
              className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57] mb-2"
              data-testid="close-pro-search"/>
            {results.length > 0 && (
              <div className="space-y-1.5 mb-3 max-h-52 overflow-y-auto">
                {results.map(p => (
                  <button key={p.user_id} onClick={() => submit("found_someone", p.user_id)} disabled={busy}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl border border-black/5 hover:border-[#245544] text-left disabled:opacity-50"
                    data-testid={`close-pick-pro-${p.user_id}`}>
                    <span className="w-8 h-8 rounded-full bg-[#F2F4F2] grid place-items-center font-bold text-xs overflow-hidden shrink-0">
                      {p.avatar_url ? <img src={p.avatar_url} className="w-full h-full object-cover" alt=""/> : p.business_name?.[0]}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-semibold text-sm truncate">{p.business_name}</span>
                      <span className="block text-xs text-[#222]/50 truncate">{p.category}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => submit("found_someone", null)} disabled={busy}
              className="w-full text-left px-4 py-3 rounded-xl border border-dashed border-black/15 text-sm font-semibold text-[#222]/70 hover:border-[#245544] disabled:opacity-50"
              data-testid="close-off-platform">Someone not on LIMYA / No Pro to select</button>
            <button onClick={() => setStep("outcome")} className="mt-3 text-sm text-[#222]/50" data-testid="close-back">← Back</button>
          </>
        )}
      </div>
    </div>
  );
}

const STATUS = {
  open: ["Open", "#245544", "#E9F0EC"],
  filled: ["Filled", "#7a6a1f", "#FBF3D6"],
  closed: ["Closed", "#6b6b6b", "#eeeeee"],
  expired: ["Expired", "#8a5a30", "#F3E7DC"],
};

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responders, setResponders] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showClose, setShowClose] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/requests/${id}`).then(res => {
      setR(res.data);
      if (res.data.is_owner) {
        api.get(`/requests/${id}/responders`).then(x => setResponders(x.data.responders || [])).catch(() => {});
      }
    }).catch(() => toast.error("Could not load request")).finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, id]);

  const reopen = async () => {
    try { await api.post(`/requests/${id}/status`, { status: "open" }); toast.success("Request reopened"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    if (!edit.category?.trim()) { toast.error("Choose a category so the right pros see your request"); return; }
    try { await api.patch(`/requests/${id}`, { title: edit.title, details: edit.details, category: edit.category }); setEdit(null); toast.success("Saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (user === null) return null;
  if (!user) return <AuthGate title="Sign in to view this request" line="Requests are visible to LIMYA members. Join free to see what your neighbors need, or sign in to continue." testId="request-detail-auth-gate"/>;
  if (loading || !r) return <div><Header/><div className="text-center py-20 text-[#222]/40">Loading…</div></div>;

  const st = STATUS[r.status] || STATUS.open;
  const isPro = user.role === "pro";
  const active = r.status === "open";
  const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div>
      <Header/><Toaster position="top-center"/>
      <div className="max-w-2xl mx-auto px-6 py-8">
        <button onClick={() => navigate("/requests")} className="text-sm text-[#245544] font-semibold inline-flex items-center gap-1 mb-4" data-testid="request-back"><ArrowLeft size={15}/> Back to Requests</button>
        <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid="request-detail">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ color: st[1], background: st[2] }} data-testid="request-status">{st[0]}</span>
            {r.category && <span className="text-xs text-[#222]/50">{r.category}</span>}
          </div>
          {edit ? (
            <input value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} className="w-full text-2xl font-bold border-b border-black/10 outline-none mb-2" style={{ fontFamily: "Outfit" }} data-testid="request-edit-title"/>
          ) : (
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }} data-testid="request-title">{r.title}</h1>
          )}
          <p className="text-xs text-[#222]/50 mb-4 flex items-center gap-1">
            <MapPin size={12}/> {r.zip ? `Near ${r.zip}` : "Local"}{isPro && r.approx_distance ? ` · ${r.approx_distance} mi away` : ""} · Posted {fmt(r.created_at)}
          </p>
          {edit ? (
            <>
              <textarea rows={4} value={edit.details} onChange={e => setEdit({ ...edit, details: e.target.value })} className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm mb-2" data-testid="request-edit-details"/>
              <div className="mb-1">
                <CategoryTypeahead
                  value={edit.category}
                  onChange={(v) => setEdit({ ...edit, category: v, category_valid: false })}
                  onSelect={(name) => setEdit({ ...edit, category: name, category_valid: true })}
                  placeholder="Category (required) — search e.g. Plumbing"
                  testId="request-edit-category"
                  inputClassName="w-full px-3 py-2 border border-black/10 rounded-xl text-sm"
                />
              </div>
              <button type="button" onClick={() => setEdit({ ...edit, category: "Other", category_valid: true })}
                className="text-[11px] font-semibold text-[#245544] underline mb-3 block" data-testid="request-edit-category-other">
                Not listed? Use Other
              </button>
              <div className="flex gap-2">
                <button onClick={saveEdit} className="btn-lime px-4 py-2 text-sm" data-testid="request-edit-save">Save</button>
                <button onClick={() => setEdit(null)} className="px-4 py-2 text-sm">Cancel</button>
              </div>
            </>
          ) : (
            <>
              {r.details && <p className="text-sm text-[#222]/80 whitespace-pre-wrap mb-4" data-testid="request-details">{r.details}</p>}
              {r.image_urls?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {r.image_urls.map((u, i) => <img key={i} src={u} className="rounded-xl object-cover w-full h-24" alt=""/>)}
                </div>
              )}
            </>
          )}

          {!edit && (
            <div className="flex flex-wrap gap-2 pt-3 border-t border-black/5">
              {isPro ? (
                active ? (
                  <button onClick={() => navigate(`/messages?to=${r.client.user_id}&request=${r.id}`)} className="btn-lime px-5 py-2.5 text-sm inline-flex items-center gap-1.5" data-testid="request-message-client"><MessageSquare size={15}/> Message Client</button>
                ) : (
                  <p className="text-sm text-[#222]/50" data-testid="request-inactive-note">This request is {r.status} and is no longer accepting new responses.</p>
                )
              ) : (
                <>
                  {active && <button onClick={() => setEdit({ title: r.title, details: r.details || "", category: r.category || "" })} className="px-4 py-2 text-sm rounded-full border border-black/10 inline-flex items-center gap-1.5" data-testid="request-edit-btn"><Pencil size={14}/> Edit</button>}
                  {active
                    ? <button onClick={() => setShowClose(true)} className="btn-lime px-5 py-2.5 text-sm inline-flex items-center gap-1.5" data-testid="request-close"><XCircle size={14}/> Close Request</button>
                    : <button onClick={reopen} className="px-4 py-2 text-sm rounded-full border border-black/10 inline-flex items-center gap-1.5" data-testid="request-reopen"><RotateCcw size={14}/> Reopen Request</button>}
                </>
              )}
            </div>
          )}
        </div>

        {r.is_owner && (
          <div className="mt-6" data-testid="request-responders">
            <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Pros who responded ({responders.length})</h3>
            {responders.length === 0 ? (
              <p className="text-sm text-[#222]/50">No responses yet. Relevant local Pros can message you here.</p>
            ) : (
              <div className="space-y-2">
                {responders.map(p => (
                  <div key={p.user_id} className="bg-white rounded-xl border border-black/5 p-3 flex items-center gap-3" data-testid={`responder-${p.user_id}`}>
                    <div className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center font-bold shrink-0">
                      {p.avatar_url ? <img src={p.avatar_url} className="w-full h-full rounded-full object-cover" alt=""/> : p.business_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{p.business_name}</p>
                      <p className="text-xs text-[#222]/50 truncate">{p.last_message}</p>
                    </div>
                    <button onClick={() => navigate(`/messages?to=${p.user_id}&request=${r.id}`)} className="text-sm font-semibold text-[#245544] shrink-0" data-testid={`responder-open-${p.user_id}`}>Open →</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showClose && <CloseRequestModal requestId={id} responders={responders} onClose={() => setShowClose(false)} onDone={() => { setShowClose(false); load(); }}/>}
    </div>
  );
}
